import axios from "src/utils/axios";
import { createSlice, current } from "@reduxjs/toolkit";
import { map } from "lodash";
import notifyme from "src/utils/notifyme";
import { dedupeInFlight } from "src/utils/staleGuard";

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  createdSuccess: false,
  notifications: [],
  adminNotifications: [],
  notification: null,
  notificationsReady: [],
  showModal: false,
};

const slice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET NOTIFICATIONS
    getNotificationsSuccess(state, action) {
      const notifications = action.payload;
      state.notifications = notifications == null ? [] : notifications;
    },

    createNotificationSuccess(state, action) {
      state.createdSuccess = true;
      //state.notifications = [...state.notifications, action.payload];
    },

    createAdminNotificationsSuccess(state, action) {
      state.createdSuccess = true;
      state.notifications = [...state.notifications, action.payload];
    },

    addNotificationsReady(state, action) {
      var index = state.notificationsReady.length + 1;
      state.notificationsReady = [
        ...state.notificationsReady,
        {
          ...action.payload,
          readed: false,
          index: index,
        },
      ];

      state.notifications = [
        {
          ...action.payload,
          isUnRead: true,
          isOrder: true,
          targetId: index,
          type: "order_shipped",
          title: "Pedido listo",
          description: "Tu pedido esta listo para ser recogido",
        },
        ...state.notifications,
      ];
    },

    markNotificationAsRead(state, action) {
      state.notificationsReady = state.notificationsReady.map(
        (notification) => {
          return { ...notification, readed: true };
        }
      );
    },

    // MARK ALL AS READ
    markAllAsRead(state, action) {
      const markAll = state.notifications.map((notification) => {
        if (notification?.isOrder) return notification;

        return {
          ...notification,
          isUnRead: false,
        };
      });

      state.notifications = markAll;
    },
    markOneAsRead(state, action) {
      state.notifications = state.notifications.map((notification) => {
        if (action.payload === notification.id) {
          return { ...notification, isNotified: true, isUnRead: false };
        } else return notification;
      });
    },
    markOneAsNotified(state, action) {
      const markAll = state.notifications.map((notification) => {
        if (action.payload.id === notification.id)
          return {
            ...notification,
            isNotified: true,
          };
        else return notification;
      });

      state.notifications = markAll;
    },
    cleanNotificationsSuccess(state, action) {
      state.isLoading = false;
      state.notifications = [];
    },
    receiveNotificationFromHubSuccess(state, action) {
      state.notifications = [action.payload, ...state.notifications];
    },
    deleteNotification(state, action) {
      state.adminNotifications = state.adminNotifications.map(
        (notification) => {
          if (notification.id === action.payload) {
            return {
              ...notification,
              isActive: false,
              deletionDate: new Date(),
            };
          }

          return notification;
        }
      );
    },

    getAdminNotificationsSuccess(state, action) {
      const notifications = action.payload;
      state.adminNotifications = notifications == null ? [] : notifications;
    },

    activateNotification(state, action) {
      state.adminNotifications = state.adminNotifications.map(
        (notification) => {
          if (notification.id === action.payload) {
            return {
              ...notification,
              isActive: true,
            };
          }

          return notification;
        }
      );
    },

    cleanNotification(state, action) {
      state.notification = null;
    },
    getNotificationByIdSuccess(state, action) {
      state.notification = state.adminNotifications.find(
        (n) => n.id == action.payload
      );
    },

    showModalNotificationStartup(state, action) {
      state.showModal = action.payload;
    },

    markAdminOneAsRead(state, action) {
      state.adminNotifications = state.adminNotifications.map(
        (notification) => {
          if (action.payload === notification.id) {
            return { ...notification, isReaded: true, isUnRead: false };
          } else return notification;
        }
      );
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  markAllAsRead,
  createNotificationSuccess,
  createAdminNotificationsSuccess,
  getNotificationsSuccess,
  cleanNotificationsSuccess,
  markOneAsRead,
  markOneAsNotified,
  receiveNotificationFromHubSuccess,
  hasError,
  addNotificationsReady,
  markNotificationAsRead,
  deleteNotification,
  cleanNotification,
  getNotificationByIdSuccess,
  showModalNotificationStartup,
  getAdminNotificationsSuccess,
} = slice.actions;

// ----------------------------------------------------------------------

export function getNotifications() {
  return async (dispatch) => {
    const user = window.localStorage.getItem("userId");
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`notificaciones/restaurante/${user}`, {
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${window.localStorage.getItem("accessToken")}`,
        },
      });
      dispatch(
        slice.actions.getNotificationsSuccess(
          response.data.notifications.filter((n) => n.isForRestaurante)
        )
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getNotificationFromSession(session) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`notificaciones/session/${session}`, {
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${window.localStorage.getItem("accessToken")}`,
        },
      });
      dispatch(
        slice.actions.getNotificationsSuccess(response.data.notifications)
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getNotificationFromMesa() {
  return async (dispatch) => {
    const mesa = window.localStorage.getItem("mesaId");
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`notificaciones/mesa/${mesa}`);
      dispatch(
        slice.actions.getNotificationsSuccess(response.data.notifications)
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function readNotification(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.markOneAsRead(id));
      const response = await axios.get(`notificaciones/read/${id}`);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function sendNotificationToPhone(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.markOneAsNotified({ id }));
      await axios.get(`notificaciones/notify/${id}`);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function readAllNotifications(notificationes) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.markAllAsRead());

      await axios.post(
        `notificaciones/acceptall`,
        JSON.stringify(notificationes),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createNotification(notification) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.createNotificationSuccess());
      await axios.post(
        "notificaciones",
        JSON.stringify({
          title: notification.title,
          description: notification.descripcion,
          isUnRead: notification.isUnRead,
          mesaId: notification.mesaId,
          IdRestaurante: notification.IdRestaurante,
          avater: notification.avatar,
          type: notification.type,
          sessionId: notification.idSession,
          isForMesa: notification.isForMesa,
          isForRestaurante: notification.isForRestaurante,
        }),
        {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.log(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function cleanNotifications() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.cleanNotificationsSuccess());
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------
// ADMIN SYSTEM NOTIFICAITONS
// ----------------------------------------------------------------------

export function getAdminNotifications() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`notificaciones/getAdmin`);
      dispatch(slice.actions.getAdminNotificationsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createAdminNotifications(notification) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(
        "notificaciones/addAdmin",
        notification
      );
      dispatch(slice.actions.createAdminNotificationsSuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.log(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function updateAdminNotifications(notification) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.put(
        "notificaciones/updateAdmin",
        notification
      );
      dispatch(slice.actions.createAdminNotificationsSuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.log(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function deleteAdminNotifications(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.delete(`notificaciones/deleteAdmin/${id}`);
      dispatch(slice.actions.deleteNotification(id));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.log(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function activateAdminNotifications(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.delete(`notificaciones/deleteAdmin/${id}`);
      dispatch(slice.actions.activateNotification(id));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.log(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function readAdminNotification() {
  return async (dispatch) => {
    // 2026-09-11 · dedupeInFlight: AlertsCenter + Notifications component
    // + Notifications legacy dispatchan este mismo thunk en el mismo tick
    // al montar el dashboard. HAR mostraba 3 GETs seguidos. Con dedupe
    // solo 1 sale al backend, los otros 2 esperan la misma promise.
    return dedupeInFlight("notification/readAdmin", async () => {
      dispatch(slice.actions.startLoading());
      try {
        const response = await axios.get(`notificaciones/readAdmin`, {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${window.localStorage.getItem("accessToken")}`,
          },
        });

        dispatch(slice.actions.getAdminNotificationsSuccess(response.data));
      } catch (error) {
        dispatch(slice.actions.hasError(error));
      }
    });
  };
}

export function markOneAdminAsRead(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.markAdminOneAsRead(id));
      // 2026-08-27 · POST (era GET). El GET podía cachearse en el
      // navegador → 200 sin llegar al backend → NotificationUser
      // seguía IsReaded=false → la notificación volvía a aparecer al
      // relogin. Con POST + skipIdempotency ninguna capa cachea y el
      // backend siempre procesa la mutación.
      await axios.post(`notificaciones/MarkAsRead/${id}`, null, {
        skipDedupe: true,
        skipIdempotency: true,
      });
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
